import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { formatDay } from "../lib/finance";
import {
  CURRENCIES, PERIODICITY_LABELS, billingError, canWrite, contentPayload, editorPeriods, priceText, publishProblem,
} from "../lib/billing";
import { Field } from "../components/billing/BillingBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const VERSION_STATUS = { draft: "Brouillon", published: "Publiée", deprecated: "Remplacée" };

function draftState(version) {
  const c = version?.content || {};
  return {
    currency: c.currency || "XOF", onQuote: !!c.on_quote, periods: editorPeriods(c), users: c.users_included ?? "",
    featuresText: (c.features || []).join("\n"), conditions: c.conditions || "", trialDays: c.trial_days ?? "", effectiveDate: c.effective_date || "",
  };
}

function Summary({ version }) {
  const c = version.content;
  return (
    <dl className="divide-y divide-slate-100">
      <Field label="Prix">
        {c.on_quote ? "Sur devis : le prix se fixe sur chaque abonnement" : c.periods.map((p) => <span key={p.periodicity} className="block tabular-nums">{priceText(p.price_minor, c.currency, p.periodicity)}</span>)}
      </Field>
      {c.on_quote && <Field label="Périodicités">{c.periods.map((p) => PERIODICITY_LABELS[p.periodicity]).join(", ")}</Field>}
      {c.users_included && <Field label="Utilisateurs inclus">{c.users_included}</Field>}
      {c.trial_days && <Field label="Essai proposé">{c.trial_days} jours</Field>}
      {c.features?.length > 0 && <Field label="Fonctionnalités"><ul className="list-disc pl-4 space-y-0.5">{c.features.map((f) => <li key={f}>{f}</li>)}</ul></Field>}
      {c.conditions && <Field label="Conditions"><span className="whitespace-pre-line">{c.conditions}</span></Field>}
      <Field label="En vigueur depuis">{formatDay(c.effective_date)}</Field>
    </dl>
  );
}

export default function BillingOfferEditor() {
  const { offerId } = useParams();
  const { user } = useAuth();
  const canManage = canWrite(user, "billing:manage_offers");
  const canDelete = canManage && hasPermission(user, "billing:delete");
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(null);
  const [offer, setOffer] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);

  const draftVersion = useMemo(() => offer?.versions?.find((v) => v.status === "draft"), [offer]);
  const apply = useCallback((data) => {
    setOffer(data);
    setDraft(draftState(data.versions.find((v) => v.status === "draft")));
  }, []);

  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const { data } = await api.get(`/billing/offers/${offerId}`);
      apply(data);
      setPhase("ready");
    } catch (e) {
      setError(billingError(e, "Impossible de charger cette offre."));
      setPhase("error");
    }
  }, [offerId, apply]);
  useEffect(() => { load(); }, [load]);

  const run = async (label, request, success) => {
    setBusy(label);
    try {
      const { data } = await request();
      apply(data);
      if (success) toast.success(success);
      return true;
    } catch (e) {
      toast.error(billingError(e, "Enregistrement impossible. Réessayez."));
      return false;
    } finally {
      setBusy("");
    }
  };
  const save = () => run("save", () => api.put(`/billing/offers/${offerId}/versions/${draftVersion.id}`,
    { expected_version: draftVersion.version, ...contentPayload(draft) }), "Brouillon enregistré");
  const publish = async () => {
    // The draft is saved first (its latest typing), then published with the version the save returned.
    setBusy("publish");
    try {
      const { data: saved } = await api.put(`/billing/offers/${offerId}/versions/${draftVersion.id}`,
        { expected_version: draftVersion.version, ...contentPayload(draft) });
      const fresh = saved.versions.find((v) => v.id === draftVersion.id);
      const { data } = await api.post(`/billing/offers/${offerId}/versions/${draftVersion.id}/publish`, { expected_version: fresh.version });
      apply(data);
      toast.success(`Version ${fresh.number} publiée`);
    } catch (e) {
      toast.error(billingError(e, "Publication impossible. Réessayez."));
      load();
    } finally {
      setBusy("");
      setConfirm(null);
    }
  };
  const discard = () => run("discard", () => api.post(`/billing/offers/${offerId}/versions/${draftVersion.id}/discard`,
    { expected_version: draftVersion.version }), "Brouillon supprimé").then(() => setConfirm(null));
  const newVersion = () => run("new", () => api.post(`/billing/offers/${offerId}/versions`), "Nouvelle version commencée");
  const toggleArchive = () => run("archive", () => api.patch(`/billing/offers/${offerId}`, { expected_version: offer.version, active: !offer.active }),
    offer.active ? "Offre archivée" : "Offre remise en vente").then(() => setConfirm(null));

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-40 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{error}</p>
        <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
      </Card>
    );
  }
  const setPeriod = (periodicity, change) => setDraft((d) => ({ ...d, periods: d.periods.map((p) => (p.periodicity === periodicity ? { ...p, ...change } : p)) }));
  const problem = draft ? publishProblem(draft) : null;
  const history = offer.versions.filter((v) => v.status !== "draft");

  return (
    <div className="space-y-4" data-testid="billing-offer-page">
      <Link to="/revenus/offres" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={14} />Offres</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">{offer.name}</h1>
          <p className="text-sm text-slate-500">
            {offer.published ? `Version ${offer.published.number} en vente` : "Pas encore publiée"} · {offer.running_subscriptions} abonnement{offer.running_subscriptions > 1 ? "s" : ""} en cours
            {!offer.active && " · archivée (ne se vend plus)"}
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            {!draftVersion && <Button variant="outline" onClick={newVersion} disabled={!!busy} data-testid="billing-offer-new-version">Nouvelle version</Button>}
            <Button variant="outline" onClick={() => setConfirm("archive")} disabled={!!busy} data-testid="billing-offer-archive">
              {offer.active ? "Archiver" : "Remettre en vente"}
            </Button>
            {canDelete && !offer.published && (
              <Button variant="outline" className="text-red-700" onClick={() => setDeleting(offer)} disabled={!!busy} data-testid="billing-offer-delete">Supprimer…</Button>
            )}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_20rem] gap-4 items-start">
      <div className="space-y-4 min-w-0">
      {draftVersion && canManage && draft && (
        <Card className="p-4 space-y-4" data-testid="billing-offer-draft">
          <div>
            <h2 className="font-semibold text-slate-900">Brouillon — version {draftVersion.number}</h2>
            <p className="text-sm text-slate-500">Une version publiée ne se modifie plus. Les abonnements en cours gardent la version qu'ils ont prise.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="billing-offer-currency">Devise</Label>
              <Select value={draft.currency} onValueChange={(v) => setDraft((d) => ({ ...d, currency: v }))}>
                <SelectTrigger id="billing-offer-currency" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c === "XOF" ? "XOF (franc CFA)" : c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 sm:pt-6">
              <Switch id="billing-offer-quote" checked={draft.onQuote} onCheckedChange={(v) => setDraft((d) => ({ ...d, onQuote: v }))} data-testid="billing-offer-quote" />
              <Label htmlFor="billing-offer-quote">Sur devis (prix fixé sur chaque abonnement)</Label>
            </div>
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-900 mb-1">Périodicités proposées</legend>
            {draft.periods.map((p) => (
              <div key={p.periodicity} className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 w-40 text-sm">
                  <Checkbox checked={p.enabled} onCheckedChange={(v) => setPeriod(p.periodicity, { enabled: !!v })} data-testid={`billing-period-${p.periodicity}`} />
                  {PERIODICITY_LABELS[p.periodicity]}
                </label>
                {!draft.onQuote && p.enabled && (
                  <Input value={p.price} onChange={(e) => setPeriod(p.periodicity, { price: e.target.value })} inputMode="decimal" className="h-10 w-48"
                    aria-label={`Prix ${PERIODICITY_LABELS[p.periodicity].toLowerCase()} (${draft.currency})`} placeholder={`Prix en ${draft.currency}`}
                    data-testid={`billing-price-${p.periodicity}`} />
                )}
              </div>
            ))}
          </fieldset>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="billing-offer-users">Utilisateurs inclus</Label>
              <Input id="billing-offer-users" value={draft.users} onChange={(e) => setDraft((d) => ({ ...d, users: e.target.value.replace(/\D/g, "") }))} inputMode="numeric" className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="billing-offer-trial">Essai proposé (jours)</Label>
              <Input id="billing-offer-trial" value={draft.trialDays} onChange={(e) => setDraft((d) => ({ ...d, trialDays: e.target.value.replace(/\D/g, "") }))} inputMode="numeric" className="h-10" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="billing-offer-effective">En vigueur le</Label>
              <Input id="billing-offer-effective" type="date" value={draft.effectiveDate} onChange={(e) => setDraft((d) => ({ ...d, effectiveDate: e.target.value }))} className="h-10" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="billing-offer-features">Fonctionnalités (une par ligne)</Label>
            <Textarea id="billing-offer-features" value={draft.featuresText} onChange={(e) => setDraft((d) => ({ ...d, featuresText: e.target.value }))} rows={4} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="billing-offer-conditions">Conditions</Label>
            <Textarea id="billing-offer-conditions" value={draft.conditions} onChange={(e) => setDraft((d) => ({ ...d, conditions: e.target.value }))} rows={3} maxLength={4000} />
          </div>
          {problem && <p className="text-sm text-amber-700" data-testid="billing-offer-problem">{problem}</p>}
          <div className="flex flex-wrap gap-2 justify-end">
            {offer.versions.length > 1 && (
              <Button variant="ghost" onClick={() => setConfirm("discard")} disabled={!!busy} data-testid="billing-offer-discard">Supprimer le brouillon</Button>
            )}
            <Button variant="outline" onClick={save} disabled={!!busy} data-testid="billing-offer-save">
              {busy === "save" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer le brouillon
            </Button>
            <Button onClick={() => setConfirm("publish")} disabled={!!busy || !!problem} data-testid="billing-offer-publish">Publier</Button>
          </div>
        </Card>
      )}

      {offer.published && (
        <Card className="p-4 space-y-2" data-testid="billing-offer-published">
          <h2 className="font-semibold text-slate-900">En vente — version {offer.published.number}</h2>
          <Summary version={offer.published} />
        </Card>
      )}
      </div>

      <div className="space-y-4 min-w-0">
      {history.length > 0 && (
        <Card className="p-4 space-y-2">
          <h2 className="font-semibold text-slate-900">Versions</h2>
          <ul className="text-sm divide-y divide-slate-100">
            {history.map((v) => (
              <li key={v.id} className="py-2 space-y-0.5">
                <div>Version {v.number} · {VERSION_STATUS[v.status]}</div>
                <div className="text-xs text-slate-500">publiée le {formatDay(v.published_at)}{v.deprecated_at ? ` · remplacée le ${formatDay(v.deprecated_at)}` : ""}</div>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card className="p-4 space-y-2 text-sm text-slate-600">
        <h2 className="font-semibold text-slate-900">Comment ça marche</h2>
        <p>Une offre se vend dans sa version publiée. Pour changer un prix ou un contenu, créez une nouvelle version : les abonnements en cours gardent celle qu'ils ont prise.</p>
        <p>Une offre « sur devis » n'a pas de prix : il est fixé sur chaque abonnement.</p>
        <p>L'essai proposé pré-remplit la fin d'essai d'un nouvel abonnement ; il reste modifiable abonnement par abonnement.</p>
      </Card>
      </div>
      </div>

      <DeleteWithReasonDialog
        target={deleting} testId="delete-offer" onClose={() => setDeleting(null)}
        title={`Supprimer l'offre « ${deleting?.name || ""} » ?`}
        description="Elle va dans la corbeille et se restaure pendant 30 jours. Une offre déjà publiée ou vendue ne peut pas être supprimée : archivez-la à la place."
        onConfirm={async (reason) => {
          const target = deleting;
          try {
            await api.delete(`/billing/offers/${target.id}`, { data: { reason } });
            toast.success("Mise à la corbeille");
            navigate("/revenus/offres");
          } catch (e) {
            setDeleting(null);
            toast.error(billingError(e));
          }
        }}
      />
      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent data-testid="billing-offer-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "publish" ? `Publier la version ${draftVersion?.number} ?` : confirm === "discard" ? "Supprimer ce brouillon ?" : offer.active ? `Archiver « ${offer.name} » ?` : `Remettre « ${offer.name} » en vente ?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "publish" && "Elle devient la version proposée aux nouveaux abonnements. Elle ne se modifiera plus ; les abonnements en cours gardent leur version."}
              {confirm === "discard" && "Les modifications de ce brouillon seront perdues. La version publiée reste en vente."}
              {confirm === "archive" && (offer.active ? "Elle ne se vendra plus. Les abonnements en cours continuent normalement." : "Elle pourra de nouveau être choisie pour un abonnement.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); if (confirm === "publish") publish(); else if (confirm === "discard") discard(); else toggleArchive(); }}
              disabled={!!busy} data-testid="billing-offer-confirm-submit">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}
              {confirm === "publish" ? "Publier" : confirm === "discard" ? "Supprimer" : offer.active ? "Archiver" : "Remettre en vente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
